import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, TrendingUp, Trophy, Quote, Award, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import LeadForm from "@/components/LeadForm";
import JVCalculatorMini from "@/components/JVCalculatorMini";
import { api, fmtFJD } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1920&q=85";

export default function HallOfFame() {
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/case-studies").then(({ data }) => setStudies(data.items || [])).finally(() => setLoading(false));
    }, []);

    const totalUplift = studies.reduce((acc, s) => acc + ((s.final_payout_fjd || 0) - (s.original_asking_fjd || 0)), 0);
    const avgUpliftPct = studies.length ? Math.round(studies.reduce((a, s) => a + (s.uplift_pct || 0), 0) / studies.length) : 0;

    return (
        <div data-testid="hall-of-fame-page">
            {/* HERO */}
            <section className="relative min-h-[60vh] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_BG}')` }} />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 bg-ocean-deep/40" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-white w-full animate-fade-up">
                    <div className="flex items-center gap-2 mb-4">
                        <Trophy className="h-5 w-5 text-turquoise" />
                        <div className="overline text-xs uppercase tracking-[0.3em] text-turquoise font-app font-semibold">Hall of Fame</div>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-5 max-w-4xl">
                        Real Fijian landowners. <br />
                        <span className="italic text-turquoise">Real upside. Real numbers.</span>
                    </h1>
                    <p className="font-app text-lg text-white/90 max-w-2xl">
                        Closed Joint Ventures — with the landowners' permission. Asking price → final payout → uplift, on the record.
                    </p>
                </div>
            </section>

            {/* Headline metrics */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
                <div className="grid sm:grid-cols-3 gap-4">
                    <Card className="p-6">
                        <Award className="h-5 w-5 text-coral mb-2" />
                        <div className="font-display text-4xl font-bold">{studies.length}</div>
                        <div className="text-xs uppercase tracking-wider font-app text-muted-foreground">Closed Joint Ventures on record</div>
                    </Card>
                    <Card className="p-6">
                        <TrendingUp className="h-5 w-5 text-fiji-green mb-2" />
                        <div className="font-display text-4xl font-bold text-fiji-green">+{avgUpliftPct}%</div>
                        <div className="text-xs uppercase tracking-wider font-app text-muted-foreground">Average landowner uplift</div>
                    </Card>
                    <Card className="p-6">
                        <Sparkles className="h-5 w-5 text-coral mb-2" />
                        <div className="font-display text-3xl font-bold">{fmtFJD(totalUplift)}</div>
                        <div className="text-xs uppercase tracking-wider font-app text-muted-foreground">Extra value created vs outright sale</div>
                    </Card>
                </div>
            </section>

            {/* Case studies */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 space-y-16">
                {loading && <p className="text-muted-foreground font-app">Loading…</p>}
                {!loading && studies.length === 0 && (
                    <div className="text-center py-16">
                        <Trophy className="h-12 w-12 mx-auto text-coral mb-4" />
                        <h3 className="font-display text-2xl font-bold mb-2">First closed Joint Venture coming soon</h3>
                        <p className="text-muted-foreground font-app">Our deal team is structuring the first wave of Joint Ventures. Watch this space.</p>
                    </div>
                )}

                {studies.map((cs, idx) => (
                    <article key={cs.id} data-testid={`case-study-${cs.id}`} className="grid lg:grid-cols-2 gap-10 items-center">
                        <div className={idx % 2 === 1 ? "lg:order-2" : ""}>
                            <div className="grid grid-cols-2 gap-2 rounded-2xl overflow-hidden shadow-xl">
                                <div className="relative aspect-[4/5]">
                                    <img src={cs.before_photo_url} alt="Before" className="w-full h-full object-cover" loading="lazy" />
                                    <Badge className="absolute top-3 left-3 bg-volcanic/85 text-white border-0 uppercase text-[10px]">Before</Badge>
                                </div>
                                <div className="relative aspect-[4/5]">
                                    <img src={cs.after_photo_url} alt="After" className="w-full h-full object-cover" loading="lazy" />
                                    <Badge className="absolute top-3 left-3 bg-fiji-green text-white border-0 uppercase text-[10px]">After</Badge>
                                </div>
                            </div>
                        </div>

                        <div className={idx % 2 === 1 ? "lg:order-1" : ""}>
                            <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <Badge className="bg-turquoise/20 text-ocean dark:text-turquoise border-0 uppercase text-[10px]">{cs.project_type}</Badge>
                                <Badge variant="outline" className="text-[10px] uppercase">Closed {cs.year_completed}</Badge>
                                <Badge variant="outline" className="text-[10px]">{cs.timeline_months} mo</Badge>
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-2">{cs.title}</h2>
                            <div className="flex items-center gap-1 text-muted-foreground font-app text-sm mb-6">
                                <MapPin className="h-4 w-4 text-coral" /> {cs.location_region}, {cs.location_island}
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-6">
                                <Card className="p-4">
                                    <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">Asking price</div>
                                    <div className="font-display text-xl font-bold text-foreground">{fmtFJD(cs.original_asking_fjd)}</div>
                                </Card>
                                <Card className="p-4 border-fiji-green border-2">
                                    <div className="text-[10px] uppercase tracking-wider font-app text-fiji-green">Landowner payout</div>
                                    <div className="font-display text-xl font-bold text-fiji-green">{fmtFJD(cs.final_payout_fjd)}</div>
                                </Card>
                                <Card className="p-4 bg-sunset text-white">
                                    <div className="text-[10px] uppercase tracking-wider font-app text-white/85">Uplift</div>
                                    <div className="font-display text-xl font-bold">+{cs.uplift_pct}%</div>
                                </Card>
                            </div>

                            <p className="font-app text-foreground/80 leading-relaxed mb-6">{cs.story}</p>

                            {cs.landowner_quote && (
                                <Card className="p-5 bg-sand/50 dark:bg-secondary/40 border-l-4 border-coral mb-5">
                                    <Quote className="h-5 w-5 text-coral mb-2" />
                                    <p className="font-display italic text-lg leading-relaxed">"{cs.landowner_quote}"</p>
                                    {cs.landowner_name && <div className="text-sm font-app text-muted-foreground mt-2">— {cs.landowner_name}</div>}
                                </Card>
                            )}

                            {/* Inline mini JV calculator — pre-loaded with this case-study's asking price */}
                            <div className="mt-2">
                                <div className="font-app text-[10px] uppercase tracking-[0.25em] font-bold text-coral mb-2">
                                    See the +{cs.uplift_pct}% uplift for yourself
                                </div>
                                <JVCalculatorMini
                                    defaultPrice={cs.original_asking_fjd || 500000}
                                    defaultProjectType={cs.project_type || "tourism"}
                                    caseStudyId={cs.id}
                                />
                            </div>
                        </div>
                    </article>
                ))}
            </section>

            {/* LEAD FORM */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-8" data-testid="hof-lead-form-section">
                <LeadForm
                    source="hall_of_fame"
                    title="Want to be the next case study?"
                    subtitle="Tell us about your land. Our deal team will model the potential Joint Venture uplift and reach out within 1 business day with a free no-obligation valuation."
                />
            </section>

            {/* CTA */}
            <section className="bg-ocean-deep-grad text-white py-20">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <Trophy className="h-12 w-12 mx-auto text-turquoise mb-6" />
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                        Want to be the next case study?
                    </h2>
                    <p className="font-app text-white/85 max-w-2xl mx-auto mb-8">
                        Submit your land for a Joint Venture proposal. Our deal team will model the uplift and find you the right partner.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Button asChild size="lg" data-testid="hof-cta-jv-btn" className="bg-coral hover:bg-coral-deep text-white font-app">
                            <Link to="/jv/submit">Submit a Joint Venture proposal <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-app">
                            <Link to="/how-it-works">How Joint Ventures work</Link>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
